import posthog from 'posthog-js'

import { GET_BUILD } from 'swUpdate'

import { decideReload, UpdateDecision } from './decide'

export { decideReload, MIN_RELOAD_GAP_MS } from './decide'
export type { UpdateDecision, UpdateEnv, UpdateReason } from './decide'

const LAST_BUILD_KEY = 'app-update:build'
const LAST_RELOAD_KEY = 'app-update:reloaded-at'

/** A worker that predates this file never answers; don't hold the page up. */
const BUILD_REPLY_TIMEOUT_MS = 1500

/** Ask the waiting worker which build it was compiled for. '' = no answer. */
const askBuildId = (worker: ServiceWorker): Promise<string> =>
    new Promise((resolve) => {
        let settled = false
        const finish = (buildId: string) => {
            if (settled) return
            settled = true
            resolve(buildId)
        }

        window.setTimeout(() => finish(''), BUILD_REPLY_TIMEOUT_MS)

        try {
            const channel = new MessageChannel()
            channel.port1.onmessage = (event) => {
                const buildId = event.data?.buildId
                finish(typeof buildId === 'string' ? buildId : '')
            }
            worker.postMessage({ type: GET_BUILD }, [channel.port2])
        } catch {
            finish('')
        }
    })

const readStorage = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

const storageWorks = (): boolean => {
    try {
        window.localStorage.getItem(LAST_BUILD_KEY)
        return true
    } catch {
        return false
    }
}

const rememberTakeover = (build: string, now: number) => {
    try {
        window.localStorage.setItem(LAST_RELOAD_KEY, String(now))
        if (build) window.localStorage.setItem(LAST_BUILD_KEY, build)
    } catch {
        // Storage is gone; the decision function already refuses to reload
        // when that happens, so the next cycle stays safe.
    }
}

const report = (decision: UpdateDecision, build: string) => {
    // Sent with sendBeacon when we are about to reload — a normal XHR would be
    // cancelled by the navigation and we would never learn how often iOS does
    // this to people.
    posthog.capture(
        'app_update_ready',
        {
            reason: decision.reason,
            reloaded: decision.reload,
            build_known: Boolean(build),
        },
        decision.reload ? { transport: 'sendBeacon' } : undefined
    )
}

/**
 * Record the build the page is already running, at startup.
 *
 * Without this the very first phantom update after this ships still reloads
 * once: nothing has ever written a build id, so there is nothing for it to
 * match. The controller is by definition the worker that served this document,
 * so its build IS the one we are on. A worker too old to answer says nothing
 * and leaves the rate limit to do the work.
 */
export const seedCurrentBuild = async (): Promise<void> => {
    const controller = navigator.serviceWorker?.controller
    if (!controller) return

    const build = await askBuildId(controller)
    if (!build) return

    try {
        window.localStorage.setItem(LAST_BUILD_KEY, build)
    } catch {
        // Nothing to do: decideReload refuses to reload without storage.
    }
}

let handled = false

/**
 * A new service worker is waiting. Let it take over, and reload the open page
 * only when that actually means a new build.
 *
 * Always hands over (`SKIP_WAITING`) so a real deploy is live for the next
 * navigation even when we choose not to reload underneath the user.
 */
export const applyAppUpdate = async (
    registration: ServiceWorkerRegistration
): Promise<void> => {
    const waiting = registration.waiting
    if (!waiting || handled) return
    handled = true

    const build = await askBuildId(waiting)
    const now = Date.now()
    const works = storageWorks()
    const lastReload = works ? readStorage(LAST_RELOAD_KEY) : null
    const decision = decideReload({
        build,
        lastBuild: works ? readStorage(LAST_BUILD_KEY) : null,
        lastReloadAt: lastReload === null ? null : Number(lastReload) || null,
        storageWorks: works,
        visible: document.visibilityState !== 'hidden',
        now,
    })

    if (decision.reload) {
        rememberTakeover(build, now)
        let reloaded = false
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (reloaded) return
            reloaded = true
            report(decision, build)
            window.location.reload()
        })
    } else {
        report(decision, build)
    }

    waiting.postMessage({ type: 'SKIP_WAITING' })
}
